import { Component, Inject, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, MatSortable } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Storage } from '@ionic/storage';
import { Subscription } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { RoleName } from 'src/app/parameters/users/shared/models';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { UtilsService } from 'src/app/shared/services/utils.service';

import { LoggedUsersService } from '../../services/logged-users.service';
import { LoggedUser, TaskDetail, TaskDetails, TaskDetailsRaw } from '../../shared/models';
import { DashboardTaskDetailsDialogComponent } from './dashboard-task-details-dialog/dashboard-task-details-dialog.component';

@Component({
  selector: 'app-dashboard-logged-users',
  templateUrl: './dashboard-logged-users.component.html',
  styleUrls: ['./dashboard-logged-users.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DashboardLoggedUsersComponent implements OnInit, OnDestroy {
  @Input() roles: RoleName[];
  @Input() routingTagItems: Choice[];
  @Input() routingTagLabel: string;

  routingTagsLocalStorageKey = `logged-users-routing-tags-${this.alcmeonContextService.companyId}`;
  rolesLocalStorageKey = `logged-users-roles-${this.alcmeonContextService.companyId}`;
  rolesItems: Choice[];
  rolesLabel: string;
  form: FormGroup = this.formBuilder.group({
    teamIds: [[]],
    roleIds: [[]],
  });
  formattedLoggedUsers: LoggedUser[] = [];
  isLoading = true;
  maxNumberTeamsToShow: number;
  teamLabelsTooltip: string;
  lastUpdatelabel: string;
  now = new Date();

  displayedColumns: string[] = [
    'id',
    'name',
    'teams',
    'assigned',
    'answered',
    'archived',
    'lastConnection',
    'lastActionDelay',
    'cumulativeConnectionTime',
    'cumulativeProcessingTime',
    'buttonLogOut',
  ];
  dataSource: MatTableDataSource<LoggedUser> = new MatTableDataSource([]);
  @ViewChild(MatSort) sort: MatSort;

  private subscriptions = new Subscription();

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private loggedUsersService: LoggedUsersService,
    private storage: Storage,
    private utilService: UtilsService,
    @Inject('TopToolbarFactory') private topToolbarService: any
  ) {}

  async ngOnInit() {
    this.topToolbarService.setButton('metric-reports/admin-user/export', () => this.exportUsersList());

    this.rolesItems = this.roles.map(role => ({ id: role.id, label: role.name }));
    this.rolesLabel = this.basicService.translate('metric-reports/admin-user/role');

    this.subscriptions.add(
      this.form.valueChanges.pipe(debounceTime(300)).subscribe(value => {
        this.updateLoggedUsersList(value);
      })
    );

    const teamIds = await this.storage.get(this.routingTagsLocalStorageKey);
    const roleIds = await this.storage.get(this.rolesLocalStorageKey);

    if (teamIds === null && roleIds === null) {
      this.updateLoggedUsersList(this.form.value);
    }

    this.sort.sort({ id: 'name', start: 'asc' } as MatSortable);
  }

  filterByName(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  private updateLoggedUsersList(filters: { roleIds: number[]; teamIds: number[] }) {
    this.isLoading = true;
    this.loggedUsersService
      .getlist()
      .pipe(map(users => users.map(this.formatLoggedUsersList)))
      .subscribe(data => {
        this.formattedLoggedUsers = this.filterUsersList(data, filters);
        this.dataSource = new MatTableDataSource(this.formattedLoggedUsers);

        this.dataSource.filterPredicate = (data, filter: string): boolean => {
          return data.name.toLowerCase().includes(filter) || `${data.id}` === filter;
        };

        this.dataSource.sort = this.sort;
        this.dataSource.sortingDataAccessor = (item, property) => {
          switch (property) {
            case 'id':
            case 'cumulativeConnectionTime':
            case 'cumulativeProcessingTime':
            case 'lastActionDelay':
              return item[property];
            case 'name':
              return item[property].toLowerCase();
            case 'assigned':
            case 'archived':
            case 'answered':
              return item[property].length;
            case 'lastConnection':
              return Date.parse(item[property]);
          }
        };

        this.isLoading = false;
      });
  }

  private formatLoggedUsersList = (user: LoggedUser): LoggedUser => {
    const formattedLastActionDelay = this.formatDelayTime(user.lastActionDelay);
    const formattedCumulativeConnectionTime = this.formatDelayTime(user.cumulativeConnectionTime);
    const formattedCumulativeProcessingTime = this.formatDelayTime(user.cumulativeProcessingTime);
    const canBeDisconnected = this.alcmeonContextService.self.id !== user.id;

    // Show more teams in tooltip
    const userShowMoreTeams = user.teamLabels.length - 2;
    const showMoreTeams =
      userShowMoreTeams > 0
        ? `${this.basicService.translate('metric-reports/admin-user/remaining-teams-to-show', {
            remainingTeamsToShow: `${userShowMoreTeams}`,
          })}`
        : '';

    if (user.teamLabels.length > 3) {
      this.maxNumberTeamsToShow = 2;
      this.teamLabelsTooltip = user.teamLabels.slice(2).join(', ');
    } else {
      this.maxNumberTeamsToShow = 3;
    }

    return {
      ...user,
      formattedLastActionDelay,
      formattedCumulativeConnectionTime,
      formattedCumulativeProcessingTime,
      canBeDisconnected,
      showMoreTeams,
    };
  };

  private formatDelayTime(seconds: number): string {
    if (!seconds) {
      return this.basicService.translate('not_applicable');
    } else {
      return this.utilService.formatDuration(seconds, false);
    }
  }

  /**
   * Filtering the list of logged users with the input from selects Team and Role
   *
   * @param users
   * @param filters
   * @returns list of logged users that are compatibile with filters
   */
  private filterUsersList(users: LoggedUser[], filters: { roleIds: number[]; teamIds: number[] }) {
    return users.filter((user: LoggedUser) => {
      for (const key in filters) {
        if (filters[key] && filters[key].length > 0) {
          if (!filters[key].some(id => user[key].includes(id))) {
            return false;
          }
        }
      }
      return true;
    });
  }

  disconnectUser(userId: number) {
    this.loggedUsersService.disconnectUser(userId).subscribe(() => {
      this.updateLoggedUsersList(this.form.value);
    });
  }

  refreshUsersList() {
    this.now = new Date();
    this.updateLoggedUsersList(this.form.value);
  }

  exportUsersList() {
    this.loggedUsersService.createExport(this.formattedLoggedUsers);
  }

  showTaskDetails(tasks: number[]) {
    if (tasks.length === 0) {
      return;
    }
    this.loggedUsersService
      .createTaskDetailsJson(tasks)
      .pipe(
        map((details: TaskDetailsRaw) => {
          const headers: TaskDetail = this.formatHeaders(details.headers);
          const rows: TaskDetail[] = details.rows.map(this.formatTaskDetails);
          const taskDetails: TaskDetails = { headers: headers, rows: rows };
          return taskDetails;
        })
      )
      .subscribe((taskDetails: TaskDetails) => {
        this.dialog.open(DashboardTaskDetailsDialogComponent, {
          panelClass: ['large-size'],
          data: {
            headers: taskDetails.headers,
            rows: taskDetails.rows,
            tasks: tasks,
          },
        });
      });
  }

  private formatHeaders(headers: string[]): TaskDetail {
    const id = headers[0];
    const dateOfReceipt = headers[1];
    const account = headers[2];
    const channel = headers[3];
    const url = headers[4];
    const content = headers[5];
    const authorId = headers[6];
    const authorName = headers[7];
    const dateOfDisplay = headers[8];
    const team = headers[9];

    return {
      id,
      dateOfReceipt,
      account,
      channel,
      url,
      content,
      authorId,
      authorName,
      dateOfDisplay,
      team,
    };
  }

  private formatTaskDetails = (row: { type: string; value: string | number }): TaskDetail => {
    const id = row[0].value;
    const dateOfReceipt = row[1].value;
    const account = row[2].value;
    const channel = row[3].value;
    const url = row[4].value;
    const content = row[5].value;
    const authorId = row[6].value;
    const authorName = row[7].value;
    const dateOfDisplay = row[8].value;
    const team = row[9].value;
    return {
      id,
      dateOfReceipt,
      account,
      channel,
      url,
      content,
      authorId,
      authorName,
      dateOfDisplay,
      team,
    };
  };

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
    this.topToolbarService.clearButtons();
  }
}
